import { useState } from "react";
import { LocalizedLink } from "@/i18n/LocalizedLink";
import { Package, ArrowRight } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { useUserAuth } from "@/contexts/UserAuthContext";
import LoginRequiredModal from "@/components/LoginRequiredModal";

const PLATFORM_COLORS = {
  Steam: "bg-[#1b2838] text-[#66c0f4] border-[#66c0f4]/30",
  Epic: "bg-black text-zinc-100 border-zinc-700",
  "Epic Games": "bg-black text-zinc-100 border-zinc-700",
  Rockstar: "bg-[#fcaf17]/10 text-[#fcaf17] border-[#fcaf17]/30",
  Ubisoft: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  EA: "bg-red-500/10 text-red-300 border-red-500/30",
  "Battle.net": "bg-[#148eff]/10 text-[#148eff] border-[#148eff]/30",
  GPT: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
};

function platformClasses(platform) {
  return PLATFORM_COLORS[platform] || "bg-brand/10 text-brand-cyan border-brand/30";
}

export default function ProductCard({ product, testid }) {
  const { user } = useUserAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const title = product.title;
  const to = `/games/${product.id}`;

  const cardInner = (
    <>
      <div className="aspect-[4/5] overflow-hidden bg-[#080808] relative">
        {product.image_url ? (
          <img
            src={imgUrl(product.image_url)}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-700">
            <Package className="w-12 h-12" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <div className="absolute top-3 start-3">
          <span className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${platformClasses(product.platform)}`}>
            {product.platform}
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-heading font-semibold text-base line-clamp-1">{title}</h3>
          <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-brand-cyan group-hover:translate-x-1 transition-all rtl-flip flex-shrink-0 mt-1" />
        </div>
        {product.description && (
          <div className="text-xs text-zinc-500 line-clamp-2">{product.description}</div>
        )}
      </div>
    </>
  );

  const tid = testid || `product-card-${product.id}`;
  const cls = "group relative bg-[#0c0c0c] border border-white/5 rounded-xl overflow-hidden card-hover block text-start w-full";

  if (user) {
    return (
      <LocalizedLink to={to} className={cls} data-testid={tid}>
        {cardInner}
      </LocalizedLink>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setLoginOpen(true)}
        className={cls}
        data-testid={tid}
      >
        {cardInner}
      </button>
      <LoginRequiredModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        next={to}
      />
    </>
  );
}
